import { createContext, useContext, useState, useCallback } from "react";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);

  const setRestaurantScope = useCallback((nextRestaurantId) => {
    setRestaurantId((prevRestaurantId) => {
      const normalizedNext = nextRestaurantId || null;
      if (!normalizedNext || (prevRestaurantId && prevRestaurantId !== normalizedNext)) {
        setItems([]);
      }
      return normalizedNext;
    });
  }, []);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.product_id === product.product_id);
      if (existing) {
        return prev.map((item) =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((item) => item.product_id !== productId));
    } else {
      setItems((prev) =>
        prev.map((item) =>
          item.product_id === productId ? { ...item, quantity } : item
        )
      );
    }
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((item) => item.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const setCartFromOrder = useCallback((orderItems, nextRestaurantId = null) => {
    if (nextRestaurantId) {
      setRestaurantId(nextRestaurantId);
    }
    setItems(
      orderItems.map((item) => ({
        product_id: item.product_id,
        name: item.product_name,
        price: item.price,
        unit: item.unit,
        quantity: item.quantity,
      }))
    );
  }, []);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        restaurantId,
        setRestaurantScope,
        setCartFromOrder,
        total,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
